import React, { useMemo } from 'react';
import type { BacktestCandle, BacktestProposal } from 'types/history';
import { fmtPrice } from './backtestFormat';

interface BacktestChartProps {
  candles: BacktestCandle[];
  proposal: BacktestProposal;
  /** Primary entry-window length, drawn as a boundary line. */
  windowN?: number;
}

// Fixed drawing surface; the SVG scales responsively via viewBox + width:100%.
const W = 720;
const H = 268;
const PAD_T = 12;
const PAD_B = 32; // two label rows: clock, then the date on day-change ticks
const PAD_L = 8;
const PAD_R = 66;
// Vertical clearance (viewBox units) a price tick needs from an SL/TP label.
const LABEL_MIN_GAP = 9;
const INNER_W = W - PAD_L - PAD_R;
const INNER_H = H - PAD_T - PAD_B;

// Aligned with theme.css tokens so the chart reads as part of the dashboard.
const UP = '#00e87a'; // --green
const DOWN = '#ff5a5a'; // --red
const STOP_COLOR = '#ff5a5a';
const TP_COLOR = '#00e87a';
const ENTRY_FILL = 'rgba(0, 191, 255, 0.16)';
const ENTRY_STROKE = 'rgba(0, 191, 255, 0.75)';
const AXIS = 'rgba(232, 234, 240, 0.38)';
const GRID = 'rgba(255,255,255,0.08)';

/** Clock portion only — the date is carried by the day-change ticks. */
function clockLabel(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString(undefined, { hour: '2-digit', minute: '2-digit' });
}

function dayLabel(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString(undefined, { month: 'short', day: 'numeric' });
}

/**
 * Round price steps (1/2/5 × 10ⁿ) spanning [min, max].
 *
 * The domain comes from real highs/lows, so evenly dividing it would print ticks
 * like 1,893.47 — a scale you cannot read a price off at a glance. Snapping to
 * round numbers is what makes the axis useful.
 */
function niceTicks(min: number, max: number, target = 5): number[] {
  const span = max - min;
  if (!(span > 0) || !Number.isFinite(span)) return [];
  const rough = span / target;
  const mag = Math.pow(10, Math.floor(Math.log10(rough)));
  const norm = rough / mag;
  const step = (norm >= 7 ? 10 : norm >= 3 ? 5 : norm >= 1.5 ? 2 : 1) * mag;
  const out: number[] = [];
  // Guard the loop against a pathological step; ticks are cosmetic, never worth a hang.
  for (let v = Math.ceil(min / step) * step; v <= max && out.length < 40; v += step) {
    out.push(Math.round(v / step) * step); // re-snap: repeated += accumulates float drift
  }
  return out;
}

/** ``count`` evenly spaced candle indices, always including first and last. */
function tickIndices(n: number, count = 6): number[] {
  if (n <= 1) return n === 1 ? [0] : [];
  const k = Math.min(count, n);
  const idx = Array.from({ length: k }, (_, i) => Math.round((i * (n - 1)) / (k - 1)));
  return Array.from(new Set(idx));
}

/**
 * Draws the forward candle window with the proposal's entry zone / stop / target
 * lines and the simulated entry + exit markers, so the verdict can be checked by
 * eye against the real candles.
 */
const EVENTUAL_STROKE = '#ffb020'; // --amber: entry that filled beyond the window

export function BacktestChart({ candles, proposal, windowN }: BacktestChartProps) {
  const model = useMemo(() => {
    if (!candles.length) return null;
    const n = candles.length;

    // Price domain across candles AND every level we draw, so nothing clips.
    const prices: number[] = [];
    for (const c of candles) prices.push(c.high, c.low);
    if (proposal.entry_zone) prices.push(proposal.entry_zone[0], proposal.entry_zone[1]);
    if (proposal.stop_loss != null) prices.push(proposal.stop_loss);
    // The proposal's own stop is drawn as a ghost when the replay widened it, so
    // it has to be in the domain too — otherwise the ghost clips off the top.
    if (proposal.stop_original != null) prices.push(proposal.stop_original);
    for (const t of proposal.targets) if (t.price != null) prices.push(t.price);
    if (proposal.fill_price != null) prices.push(proposal.fill_price);
    if (proposal.exit_price != null) prices.push(proposal.exit_price);

    let min = Math.min(...prices);
    let max = Math.max(...prices);
    if (min === max) {
      min -= 1;
      max += 1;
    }
    const pad = (max - min) * 0.06;
    min -= pad;
    max += pad;

    const slot = INNER_W / n;
    const candleW = Math.max(2, Math.min(16, slot * 0.6));
    const xCenter = (i: number) => PAD_L + (i + 0.5) * slot;
    const y = (price: number) => PAD_T + (1 - (price - min) / (max - min)) * INNER_H;

    // The proposal's own levels own the right gutter — they are the point of the
    // chart. A price tick that would collide with one is dropped rather than
    // overprinted, so the scale thins out near SL/TP instead of becoming unreadable.
    const reserved: number[] = [];
    if (proposal.stop_loss != null) reserved.push(y(proposal.stop_loss));
    if (proposal.stop_original != null) reserved.push(y(proposal.stop_original));
    for (const t of proposal.targets) if (t.price != null) reserved.push(y(t.price));
    const seenLabels = new Set<string>();
    const priceTicks = niceTicks(min, max).filter((p) => {
      if (reserved.some((ry) => Math.abs(y(p) - ry) < LABEL_MIN_GAP)) return false;
      // Below fmtPrice's precision floor (sub-micro-cap ranges) distinct ticks can
      // format identically — print each label once rather than a stack of "0".
      const label = fmtPrice(p);
      if (seenLabels.has(label)) return false;
      seenLabels.add(label);
      return true;
    });

    return { n, min, max, slot, candleW, xCenter, y, priceTicks };
  }, [candles, proposal]);

  if (!model) {
    return <div className="bt-chart-empty">No candles to plot.</div>;
  }

  const { n, candleW, xCenter, y } = model;
  const isLong = String(proposal.direction).toUpperCase() === 'LONG';

  // ── Entry marker ──────────────────────────────────────────────────────────
  // In-window fill, or — for a NO_ENTRY proposal that filled later — the
  // eventual fill (drawn in amber).
  const nev = proposal.no_entry?.eventual ?? null;
  const entryEventual = proposal.fill_bar_offset == null && nev != null;
  const fillIdx = entryEventual ? nev!.fill_bar_offset : proposal.fill_bar_offset;
  const fillPrice = entryEventual ? nev!.fill_price : proposal.fill_price;
  const entryStroke = entryEventual ? EVENTUAL_STROKE : ENTRY_STROKE;

  // ── Exit marker ───────────────────────────────────────────────────────────
  // In-window resolution, or the eventual resolution beyond the window (an OPEN
  // trade that later hit TP/SL, or a NO_ENTRY that filled late and then resolved).
  let exitIdx: number | null = null;
  let exitPrice: number | null = null;
  let exitVerdict: string = proposal.verdict;
  let exitTpLabel: string | null = proposal.tp_label;
  // True when the exit happened *outside* the scored window. The badge verdict is
  // window-scoped, so without this the chart shows an SL diamond next to an "OPEN"
  // badge and reads as a contradiction.
  let exitEventual = false;
  if (proposal.fill_bar_offset != null && proposal.outcome_bar_offset != null) {
    exitIdx = proposal.fill_bar_offset + proposal.outcome_bar_offset;
    exitPrice = proposal.exit_price;
  } else if (
    proposal.verdict === 'OPEN' &&
    proposal.eventual_exit &&
    proposal.fill_bar_offset != null &&
    proposal.eventual_exit.outcome_bar_offset != null
  ) {
    exitIdx = proposal.fill_bar_offset + proposal.eventual_exit.outcome_bar_offset;
    exitPrice = proposal.eventual_exit.exit_price;
    exitVerdict = proposal.eventual_exit.verdict;
    exitTpLabel = proposal.eventual_exit.tp_label;
    exitEventual = true;
  } else if (nev && nev.outcome_bar_offset != null) {
    exitIdx = nev.fill_bar_offset + nev.outcome_bar_offset;
    exitPrice = nev.exit_price;
    exitVerdict = nev.verdict;
    exitTpLabel = nev.tp_label;
    exitEventual = true;
  }
  const exitTone = exitVerdict === 'SL_HIT' ? DOWN : exitVerdict === 'TP_HIT' ? UP : AXIS;
  // Amber means "beyond the window" everywhere — the entry marker and the panel's
  // bt-eventual note already use it.
  const exitStroke = exitEventual ? EVENTUAL_STROKE : exitTone;

  // Boundary between the primary entry window and the extended search.
  const boundaryX = windowN != null && windowN < n ? PAD_L + windowN * model.slot : null;

  // X-axis ticks. The date is repeated only when the day changes (and always on
  // the first tick), so a multi-day window stays unambiguous without printing
  // "Jul 28" six times.
  const xTicks = tickIndices(n).map((i, k, all) => {
    const iso = candles[i].time;
    const prevIso = k > 0 ? candles[all[k - 1]].time : null;
    const day = dayLabel(iso);
    return {
      i,
      clock: clockLabel(iso),
      day: k === 0 || (prevIso && dayLabel(prevIso) !== day) ? day : null,
      anchor: (k === 0 ? 'start' : k === all.length - 1 ? 'end' : 'middle') as
        | 'start'
        | 'end'
        | 'middle',
    };
  });

  return (
    <svg className="bt-chart" viewBox={`0 0 ${W} ${H}`} role="img" aria-label="Backtest candles">
      {/* Price scale — drawn first so gridlines sit behind candles and levels. */}
      {model.priceTicks.map((p) => (
        <g key={`py-${p}`}>
          <line
            x1={PAD_L}
            x2={PAD_L + INNER_W}
            y1={y(p)}
            y2={y(p)}
            stroke={GRID}
            strokeWidth={1}
          />
          <text x={W - PAD_R + 4} y={y(p) + 3} className="bt-axis-label" fill={AXIS}>
            {fmtPrice(p)}
          </text>
        </g>
      ))}

      {/* Entry zone band */}
      {proposal.entry_zone && (
        <rect
          x={PAD_L}
          width={INNER_W}
          y={y(Math.max(proposal.entry_zone[0], proposal.entry_zone[1]))}
          height={Math.max(1, Math.abs(y(proposal.entry_zone[0]) - y(proposal.entry_zone[1])))}
          fill={ENTRY_FILL}
          stroke={ENTRY_STROKE}
          strokeDasharray="2 3"
          strokeWidth={1}
        />
      )}

      {/* The proposal's own stop, when the replay widened it. Drawn first so the
          stop actually used sits on top, and faint so it reads as context rather
          than a second live level. Without it a 2x chart looks like the analysis
          simply proposed a distant stop. */}
      {proposal.stop_original != null && proposal.stop_loss != null && proposal.stop_original !== proposal.stop_loss && (
        <g opacity={0.4}>
          <line
            x1={PAD_L}
            x2={PAD_L + INNER_W}
            y1={y(proposal.stop_original)}
            y2={y(proposal.stop_original)}
            stroke={STOP_COLOR}
            strokeDasharray="2 5"
            strokeWidth={1}
          />
          <text x={W - PAD_R + 4} y={y(proposal.stop_original) + 3} className="bt-axis-label" fill={STOP_COLOR}>
            as proposed
          </text>
        </g>
      )}

      {/* Stop line — the one the replay actually used. */}
      {proposal.stop_loss != null && (
        <g>
          <line
            x1={PAD_L}
            x2={PAD_L + INNER_W}
            y1={y(proposal.stop_loss)}
            y2={y(proposal.stop_loss)}
            stroke={STOP_COLOR}
            strokeDasharray="5 4"
            strokeWidth={1}
          />
          <text x={W - PAD_R + 4} y={y(proposal.stop_loss) + 3} className="bt-axis-label" fill={STOP_COLOR}>
            SL {fmtPrice(proposal.stop_loss)}
            {proposal.stop_mult != null && proposal.stop_mult !== 1 && ` (${proposal.stop_mult}×)`}
          </text>
        </g>
      )}

      {/* Target lines */}
      {proposal.targets.map((t, i) =>
        t.price == null ? null : (
          <g key={i}>
            <line
              x1={PAD_L}
              x2={PAD_L + INNER_W}
              y1={y(t.price)}
              y2={y(t.price)}
              stroke={TP_COLOR}
              strokeDasharray="5 4"
              strokeWidth={1}
            />
            <text x={W - PAD_R + 4} y={y(t.price) + 3} className="bt-axis-label" fill={TP_COLOR}>
              {t.label ?? `TP${i + 1}`} {fmtPrice(t.price)}
            </text>
          </g>
        ),
      )}

      {/* Candles */}
      {candles.map((c, i) => {
        const up = c.close >= c.open;
        const color = up ? UP : DOWN;
        const cx = xCenter(i);
        const yHigh = y(c.high);
        const yLow = y(c.low);
        const yO = y(c.open);
        const yC = y(c.close);
        const bodyTop = Math.min(yO, yC);
        const bodyH = Math.max(1, Math.abs(yO - yC));
        return (
          <g key={i}>
            <line x1={cx} x2={cx} y1={yHigh} y2={yLow} stroke={color} strokeWidth={1} />
            <rect x={cx - candleW / 2} y={bodyTop} width={candleW} height={bodyH} fill={color} />
          </g>
        );
      })}

      {/* Primary entry-window boundary */}
      {boundaryX != null && (
        <g>
          <line x1={boundaryX} x2={boundaryX} y1={PAD_T} y2={PAD_T + INNER_H} stroke={AXIS} strokeDasharray="2 4" strokeWidth={1} />
          {/* Bounds the entry only — a filled position runs past it to TP/SL. */}
          <text x={boundaryX + 3} y={PAD_T + 9} className="bt-axis-label" fill={AXIS}>
            entry window
          </text>
        </g>
      )}

      {/* Fill → exit connector */}
      {fillIdx != null && fillPrice != null && exitIdx != null && exitIdx < n && exitPrice != null && (
        <line
          x1={xCenter(fillIdx)}
          y1={y(fillPrice)}
          x2={xCenter(exitIdx)}
          y2={y(exitPrice)}
          stroke={exitStroke}
          strokeWidth={1}
          strokeDasharray="1 3"
        />
      )}

      {/* Entry marker (white = in-window fill, amber = eventual fill past window) */}
      {fillIdx != null && fillIdx < n && fillPrice != null && (
        <g>
          <circle
            cx={xCenter(fillIdx)}
            cy={y(fillPrice)}
            r={4.5}
            fill={entryEventual ? 'none' : '#fff'}
            stroke={entryStroke}
            strokeWidth={1.5}
          />
          {/* No "*" suffix here: the badge already uses * for ambiguous_bar, so
              amber alone carries "outside the window" (see the chart legend). */}
          <text x={xCenter(fillIdx)} y={y(fillPrice) - 8} className="bt-marker-label" textAnchor="middle">
            {isLong ? 'BUY' : 'SELL'}
          </text>
        </g>
      )}

      {/* Exit marker */}
      {exitIdx != null && exitIdx < n && exitPrice != null && (
        <g>
          <rect
            x={xCenter(exitIdx) - 4}
            y={y(exitPrice) - 4}
            width={8}
            height={8}
            fill={exitEventual ? 'none' : exitTone}
            stroke={exitEventual ? EVENTUAL_STROKE : '#000'}
            strokeWidth={exitEventual ? 1.5 : 0.5}
            transform={`rotate(45 ${xCenter(exitIdx)} ${y(exitPrice)})`}
          />
          <text x={xCenter(exitIdx)} y={y(exitPrice) + 16} className="bt-marker-label" textAnchor="middle" fill={exitStroke}>
            {exitVerdict === 'TP_HIT' ? exitTpLabel ?? 'TP' : exitVerdict === 'SL_HIT' ? 'SL' : 'exit'}
          </text>
        </g>
      )}

      {/* X axis: baseline, tick marks, clock labels, and the date whenever the day rolls over */}
      <line x1={PAD_L} x2={PAD_L + INNER_W} y1={PAD_T + INNER_H} y2={PAD_T + INNER_H} stroke={GRID} strokeWidth={1} />
      {xTicks.map(({ i, anchor, clock, day }) => {
        const cx = xCenter(i);
        // Keep the edge labels inside the plot instead of letting them overhang.
        const tx = anchor === 'start' ? PAD_L : anchor === 'end' ? PAD_L + INNER_W : cx;
        return (
          <g key={`px-${i}`}>
            <line
              x1={cx}
              x2={cx}
              y1={PAD_T + INNER_H}
              y2={PAD_T + INNER_H + 3}
              stroke={GRID}
              strokeWidth={1}
            />
            <text x={tx} y={H - 14} className="bt-axis-label" textAnchor={anchor} fill={AXIS}>
              {clock}
            </text>
            {day && (
              <text x={tx} y={H - 4} className="bt-axis-label" textAnchor={anchor} fill={AXIS}>
                {day}
              </text>
            )}
          </g>
        );
      })}
    </svg>
  );
}
